import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { getAdminPickupLocations, deletePickupLocation } from '../../services/api';
import { PickupLocation } from '../../types';
import PickupLocationModal from './components/PickupLocationModal';
import './ProductListPage.css'; // Reuse the same CSS

const PickupLocationListPage: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [locations, setLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<PickupLocation | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch pickup locations on component mount
  useEffect(() => {
    fetchPickupLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchPickupLocations = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getAdminPickupLocations(token);
      
      if (data.length > 0) {
        
        // Check if we have id or _id
        if (data[0].id !== undefined) {
          
          // Map the data to ensure _id is set
          const mappedData = data.map(location => ({
            ...location,
            _id: location.id ? location.id.toString() : undefined // Ensure _id is set from id
          }));
          
          // Sort locations by ID to maintain consistent order
          const sortedData = [...mappedData].sort((a, b) => {
            const aId = a.id || parseInt(a._id || '0');
            const bId = b.id || parseInt(b._id || '0');
            return aId - bId;
          });
          
          setLocations(sortedData);
        } else if (data[0]._id !== undefined) {          
          // Sort locations by ID to maintain consistent order
          const sortedData = [...data].sort((a, b) => {
            const aId = parseInt(a._id || '0');
            const bId = parseInt(b._id || '0');
            return aId - bId;
          });
          
          setLocations(sortedData);
        } else {
          console.error('Pickup location has neither id nor _id property');
          setError(t('admin.common.error'));
        }
      } else {
        setLocations(data);
      }
    } catch (err) {
      console.error('Error fetching pickup locations:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    setCurrentLocation(null);
    setShowModal(true);
  };

  const handleEditLocation = (location: PickupLocation) => {
    // Debug: Log the location being edited
    console.log('Attempting to edit pickup location:', location);
    
    // Validate location ID
    if (!location) {
      console.error('Cannot edit pickup location: Invalid location');
      setError(t('admin.common.error'));
      return;
    }
    
    // Check for _id or id
    const locationId = location._id || (location as any).id?.toString();
    
    if (!locationId) {
      console.error('Cannot edit pickup location: Missing ID');
      console.log('Pickup location object keys:', Object.keys(location));
      setError(t('admin.common.error'));
      return;
    }
    
    // Ensure location has _id property
    const locationWithId = {
      ...location,
      _id: locationId
    };
    
    console.log('PickupLocationListPage: Editing location with ID:', locationId);
    setCurrentLocation(locationWithId);
    setShowModal(true);
  };

  const handleDeleteLocation = async (id: string | number) => {
    // Convert id to string if it's a number
    const idString = id?.toString();
    
    
    if (!token) {
      console.error('Cannot delete pickup location: No token available');
      return;
    }
    
    if (!idString) {
      console.error('Cannot delete pickup location: Missing ID');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteId(idString);
      
      // Log the ID to verify it's valid
      console.log('Deleting pickup location with ID:', idString);
      
      await deletePickupLocation(idString, token);
      
      // Filter locations based on either _id or id
      setLocations(locations.filter(location => {
        const locationId = location._id || (location as any).id?.toString();
        return locationId !== idString;
      }));
      
      setSuccessMessage(t('admin.pickupLocations.locationDeleted'));
    } catch (err) {
      console.error('Error deleting pickup location:', err);
      setError(t('admin.common.error'));
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleModalClose = (locationUpdated: boolean = false) => {
    setShowModal(false);
    if (locationUpdated) {
      // Fetch pickup locations again to get the updated list
      fetchPickupLocations();
    }
  };

  if (loading && locations.length === 0) {
    return <div className="admin-loading">{t('admin.common.loading')}</div>;
  }

  return (
    <div className="product-list-page">
      <div className="page-header">
        <h1>{t('admin.pickupLocations.title')}</h1>
        <button 
          className="btn btn-primary add-button" 
          onClick={handleAddLocation}
        >
          {t('admin.pickupLocations.addLocation')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {locations.length === 0 ? (
        <div className="no-data-message">{t('admin.common.noData')}</div>
      ) : (
        <div className="product-table-container">
          <table className="product-table">
            <thead>
              <tr>
                <th>{t('admin.pickupLocations.locationName')}</th>
                <th>{t('admin.pickupLocations.address')}</th>
                <th>{t('admin.pickupLocations.openingHours')}</th>
                <th>{t('admin.pickupLocations.available')}</th>
                <th>{t('admin.pickupLocations.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(location => (
                <tr key={location._id}>
                  <td>{location.name}</td>
                  <td>{location.address}</td>
                  <td>{location.openingHours}</td>
                  <td>
                    <span className={`availability-badge ${location.available ? 'available' : 'unavailable'}`}>
                      {location.available ? t('admin.common.yes') : t('admin.common.no')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-action btn-edit" 
                        onClick={() => handleEditLocation(location)}
                      >
                        {t('admin.common.edit')}
                      </button>
                      <button 
                        className="btn-action btn-delete" 
                        onClick={() => handleDeleteLocation(location._id || (location as any).id)}
                        disabled={isDeleting && deleteId === (location._id || (location as any).id?.toString())}
                      >
                        {isDeleting && deleteId === (location._id || (location as any).id?.toString())
                          ? '...' 
                          : t('admin.common.delete')
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PickupLocationModal 
          location={currentLocation} 
          onClose={handleModalClose} 
        />
      )}
    </div>
  );
};

export default PickupLocationListPage;
